import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser, registerUser } from '@adornis/users/db/a-user.js';
import { changeUserPassword } from '@adornis/users/server/password.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { ensureAppUserForFieldEngineer } from '@nsc/smartteams-shared/api/create-app-user-for-fe.js';
import { normalizedEmailFilter } from '@nsc/smartteams-shared/api/email-filter.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

/**
 * Creates a user account for an existing field engineer and links them.
 * This allows a field engineer to log in after they were initially created without a user.
 */
export const createUserForFieldEngineer = registerMutation({
  operationName: 'createUserForFieldEngineer',
  type: () => String,
  params: [
    { name: 'fieldEngineerId', type: () => String },
    { name: 'password', type: () => String },
  ],
  resolve: (fieldEngineerId: string, password: string) => async () => {
    const hasPermission = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEUpdate));

    // Check permission to modify field engineers
    if (!hasPermission) {
      throw new ExpectedError('No permission to modify field engineers');
    }

    // Fetch the field engineer
    const fieldEngineer = await getByID(
      SmartTeamsFieldEngineer,
      fieldEngineerId,
    )({ _id: 1, email: 1, userId: 1, partnerCompanyIds: 1, serviceNowSysId: 1, firstLanguage: 1 });

    if (!fieldEngineer) {
      throw new ExpectedError('Field engineer not found');
    }

    // Require email to create or heal a user account
    if (!fieldEngineer.email) {
      throw new ExpectedError('Field engineer must have an email to create a user account');
    }

    // Check if user already exists
    if (fieldEngineer.userId) {
      const linkedUser = await (await getRawCollection<AdornisUser>(AdornisUser._collectionName)).findOne(
        { _id: fieldEngineer.userId },
        { projection: { _id: 1, email: 1 } },
      );

      if (linkedUser) {
        if (linkedUser.email?.toLocaleLowerCase() !== fieldEngineer.email.toLocaleLowerCase()) {
          throw new ExpectedError(
            `Linked user account email (${linkedUser.email}) differs from field engineer email (${fieldEngineer.email}) — update the field engineer email or fix the linkage manually.`,
          );
        }

        if (password) {
          AdornisUser.passwordValidator({ value: password, key: 'password', target: undefined as never });
          await changeUserPassword(fieldEngineer.userId, password, AdornisUser);
          const collection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
            SmartTeamsFieldEngineer._collectionName,
          );
          await collection.updateOne({ _id: fieldEngineerId }, { $set: { mustChangePassword: true } });
        }

        await ensureAppUserForFieldEngineer({
          userId: fieldEngineer.userId,
          email: fieldEngineer.email,
          primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
          serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
          companyId: fieldEngineer.partnerCompanyIds?.[0],
        });
        return fieldEngineer.userId;
      }
    }

    const userCollection = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    const matchingUsers = await userCollection
      .find(normalizedEmailFilter(fieldEngineer.email))
      .limit(2)
      .toArray();
    if (matchingUsers.length > 1) {
      throw new ExpectedError(
        `Duplicate user accounts differing only in case exist for ${fieldEngineer.email} and must be merged manually`,
      );
    }
    if (matchingUsers.length === 1) {
      const existingUser = matchingUsers[0]!;
      const collection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
        SmartTeamsFieldEngineer._collectionName,
      );
      if (password) {
        AdornisUser.passwordValidator({ value: password, key: 'password', target: undefined as never });
        await changeUserPassword(existingUser._id, password, AdornisUser);
        await collection.updateOne(
          { _id: fieldEngineerId },
          { $set: { userId: existingUser._id, mustChangePassword: true } },
        );
      } else {
        await collection.updateOne({ _id: fieldEngineerId }, { $set: { userId: existingUser._id } });
      }

      await ensureAppUserForFieldEngineer({
        userId: existingUser._id,
        email: fieldEngineer.email,
        primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
        serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
        companyId: fieldEngineer.partnerCompanyIds?.[0],
      });
      return existingUser._id;
    }

    // Create the user
    const lowercasedEmail = fieldEngineer.email.toLocaleLowerCase();
    const userId = await registerUser({
      username: lowercasedEmail,
      email: lowercasedEmail,
      password,
      userClass: AdornisUser,
    });

    // Update the field engineer with the userId using raw collection
    const collection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
      SmartTeamsFieldEngineer._collectionName,
    );
    await collection.updateOne({ _id: fieldEngineerId }, { $set: { userId, mustChangePassword: true } });

    await ensureAppUserForFieldEngineer({
      userId,
      email: fieldEngineer.email!,
      primaryLanguage: fieldEngineer.firstLanguage ?? undefined,
      serviceNowFEId: fieldEngineer.serviceNowSysId ?? undefined,
      companyId: fieldEngineer.partnerCompanyIds?.[0],
    });

    return userId;
  },
});
